import { clamp, round } from 'lodash';
import type { Dimension } from 'types/common_types';
import type { NodeDimension } from 'types/node_types';

type PartialNodeDimension = Pick<NodeDimension, 'h' | 'w'>;

/*
 * Returns the value of a single dimension for a panned item to keep it bound within a container
 * (0, 0) is at the top left of the panned item
 */
export function panWithinContainer(
  position: number,
  containerDimension: number,
  pannedItemDimension: number
) {
  let finalPosition;

  const outOfBoundsTopOrLeft = position > 0;
  const outOfBoundsBottomOrRight = Math.abs(position) + containerDimension >= pannedItemDimension;

  if (outOfBoundsTopOrLeft) {
    finalPosition = 0;
  } else if (outOfBoundsBottomOrRight) {
    finalPosition = containerDimension - pannedItemDimension;
  } else {
    finalPosition = position;
  }

  return finalPosition;
}

/*
 * Returns the (x, y) point for the top-left of an item to center the item within a container
 */
export function panToCenter(
  containerDimensions: PartialNodeDimension,
  itemDimensions: PartialNodeDimension
) {
  const itemCenterX = itemDimensions.w / 2;
  const itemCenterY = itemDimensions.h / 2;
  const containerCenterX = containerDimensions.w / 2;
  const containerCenterY = containerDimensions.h / 2;

  return {
    x: -(itemCenterX - containerCenterX),
    y: -(itemCenterY - containerCenterY),
  };
}

/** Calculate how to resize the image to fit in the node */
export function fitImageToNode(nodeDimensions: Dimension, imageDimensions: Dimension) {
  const { height: nodeH, width: nodeW } = nodeDimensions;
  const { height: imageH, width: imageW } = imageDimensions;
  const scalingFactor = getScalingFactor(nodeDimensions, imageDimensions);

  const fitCropW = round(nodeW / (imageW * scalingFactor), 6);
  const fitCropH = round(nodeH / (imageH * scalingFactor), 6);

  return { fitCropH, fitCropW };
}

/** Get scaling factor for fitting image to node */
export function getScalingFactor(nodeDimensions: Dimension, imageDimensions: Dimension) {
  const { height: nodeH, width: nodeW } = nodeDimensions;
  const { height: imageH, width: imageW } = imageDimensions;

  const widthRatio = nodeW / imageW;
  const heightRatio = nodeH / imageH;
  return Math.max(widthRatio, heightRatio);
}

/**
 * Calculate the pan limits of a crop based on a cropped dimension
 *
 * Based on crop points being calculated as % off center, as a decimal
 */
export function clampCropPanToLimit(cropDimension: number, cropPoint: number) {
  const halfCrop = (1 - cropDimension) / 2;
  return clamp(cropPoint, 0.5 - halfCrop, 0.5 + halfCrop);
}
